import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';

const FZ = FIELDS.geoOpsZones;
const FCS = FIELDS.cameraSources;
const FGS = FIELDS.gisDataSources;

export interface GeoZone { id: number; incidentId: string; name: string; zoneType: string; geometry: GeoJSON.Geometry; status: string; color: string; opacity: number; notes: string; createdBy: string; [k: string]: unknown; }
interface CameraSource { id: number; name: string; provider: string; baseUrl: string; status: string; [k: string]: unknown; }
interface GISSource { id: number; name: string; url: string; protocol: string; status: string; featureCount: number; [k: string]: unknown; }

// Map a raw V2 API record (field-key-keyed) to a friendly GeoZone object
function mapZone(row: Record<string, unknown>): GeoZone {
  let geometry = row[FZ.geometry];
  // Geometry may come back as a JSON string — parse it
  if (typeof geometry === 'string') {
    try { geometry = JSON.parse(geometry); } catch { /* leave as string */ }
  }
  // Unwrap GeoJSON Feature → bare Geometry (seeded data uses Feature format)
  if (geometry && (geometry as any).type === 'Feature') {
    geometry = (geometry as any).geometry;
  }
  return {
    id: (row.record_id ?? row.id) as number,
    incidentId: String(row[FZ.incidentId] || ''),
    name: String(row[FZ.name] || ''),
    zoneType: String(row[FZ.zoneType] || 'custom'),
    geometry: geometry as GeoJSON.Geometry,
    status: String(row[FZ.status] || 'active'),
    color: String(row[FZ.color] || '#DC2626'),
    opacity: Number(row[FZ.opacity] ?? 0.3),
    notes: String(row[FZ.notes] || ''),
    createdBy: String(row[FZ.createdBy] || ''),
  };
}

interface GeoOpsState {
  zones: GeoZone[]; cameraSources: CameraSource[]; gisSources: GISSource[]; loading: boolean;
  fetchZones: () => Promise<void>; fetchCameraSources: () => Promise<void>; fetchGISSources: () => Promise<void>;
  createZone: (data: Record<string, unknown>) => Promise<number | null>;
  updateZone: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteZone: (id: number) => Promise<void>;
  createCameraSource: (data: Record<string, unknown>) => Promise<number | null>;
  createGISSource: (data: Record<string, unknown>) => Promise<number | null>;
  // V1 compat
  isLoading: boolean;
  loadZones: (_id?: any) => Promise<void>;
  saveZone: (data: any) => Promise<number | null>;
}

export const useGeoOpsStore = create<GeoOpsState>((set, get) => ({
  zones: [], cameraSources: [], gisSources: [], loading: false,

  fetchZones: async () => {
    set({ loading: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const filters = inc
        ? [{ field: FZ.incidentId, operator: 'eq' as const, value: String(inc.id) }]
        : [];
      const r = await queryRecords<Record<string, unknown>>(TABLES.geoOpsZones, {
        filters,
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ zones: r.records.map(mapZone) });
    } catch (e) { console.error('[Geo]', e); }
    set({ loading: false });
  },

  fetchCameraSources: async () => {
    try {
      const r = await fetchRecords<CameraSource>(TABLES.cameraSources, { limit: 50 });
      set({ cameraSources: r.records });
    } catch (e) { console.error('[Geo]', e); }
  },

  fetchGISSources: async () => {
    try {
      const r = await fetchRecords<GISSource>(TABLES.gisDataSources, { limit: 50 });
      set({ gisSources: r.records });
    } catch (e) { console.error('[Geo]', e); }
  },

  createZone: async (data) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.name) p[FZ.name] = data.name;
      if (data.zoneType) p[FZ.zoneType] = data.zoneType;
      if (data.geometry) p[FZ.geometry] = typeof data.geometry === 'object'
        ? JSON.stringify(data.geometry) : data.geometry;
      if (data.status) p[FZ.status] = data.status;
      if (data.color) p[FZ.color] = data.color;
      if (data.opacity !== undefined) p[FZ.opacity] = data.opacity;
      if (data.incidentId) p[FZ.incidentId] = data.incidentId;
      if (data.notes) p[FZ.notes] = data.notes;
      if (data.createdBy) p[FZ.createdBy] = data.createdBy;
      const r = await createRecord<Record<string, unknown>>(TABLES.geoOpsZones, p);
      await get().fetchZones();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Geo]', e); return null; }
  },

  updateZone: async (id, data) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.name !== undefined) p[FZ.name] = data.name;
      if (data.status !== undefined) p[FZ.status] = data.status;
      if (data.color !== undefined) p[FZ.color] = data.color;
      if (data.opacity !== undefined) p[FZ.opacity] = data.opacity;
      if (data.zoneType !== undefined) p[FZ.zoneType] = data.zoneType;
      if (data.notes !== undefined) p[FZ.notes] = data.notes;
      await updateRecord(TABLES.geoOpsZones, id, p);
      await get().fetchZones();
    } catch (e) { console.error('[Geo]', e); }
  },

  deleteZone: async (id) => {
    try {
      await deleteRecord(TABLES.geoOpsZones, id);
      await get().fetchZones();
    } catch (e) { console.error('[Geo]', e); }
  },

  createCameraSource: async (data) => {
    try {
      const cp: Record<string, unknown> = {};
      if (data.name) cp[FCS.name] = data.name;
      if (data.provider) cp[FCS.provider] = data.provider;
      if (data.baseUrl) cp[FCS.baseUrl] = data.baseUrl;
      if (data.status) cp[FCS.status] = data.status;
      const r = await createRecord<Record<string, unknown>>(TABLES.cameraSources, cp);
      await get().fetchCameraSources();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Geo]', e); return null; }
  },

  createGISSource: async (data) => {
    try {
      const gp: Record<string, unknown> = {};
      if (data.name) gp[FGS.name] = data.name;
      if (data.url) gp[FGS.url] = data.url;
      if (data.protocol) gp[FGS.protocol] = data.protocol;
      if (data.status) gp[FGS.status] = data.status;
      const r = await createRecord<Record<string, unknown>>(TABLES.gisDataSources, gp);
      await get().fetchGISSources();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Geo]', e); return null; }
  },

  // V1 compat aliases
  isLoading: false,
  loadZones: async (_id?: any) => { await get().fetchZones(); },
  saveZone: async (data: any) => { return get().createZone(data); },
}));
